import { formatCurrency, formatNumber, formatPercent, formatRoas, formatDecimal } from "@/lib/formatters";

export type FormatterFn = (v: number) => string;

export interface WidgetDefinition {
  id: string;
  title: string;
  category: "kpi" | "chart" | "table";
  defaultW: number;
  defaultH: number;
  minW?: number;
  minH?: number;
  maxW?: number;
  maxH?: number;
  description: string;
  crossPlatform?: boolean;
}

export interface KpiWidgetConfig {
  id: string;
  title: string;
  group: string;
  summaryField: string;
  trendField?: string;
  sparklineCompute?: (row: Record<string, number>) => number;
  formatter: FormatterFn;
  invertColors?: boolean;
}

const computeCtr = (r: Record<string, number>) => r.impressions > 0 ? (r.clicks / r.impressions) * 100 : 0;
const computeCpc = (r: Record<string, number>) => r.clicks > 0 ? r.spend / r.clicks : 0;
const computeCpm = (r: Record<string, number>) => r.impressions > 0 ? (r.spend / r.impressions) * 1000 : 0;
const computeRoas = (r: Record<string, number>) => r.spend > 0 ? r.purchaseValue / r.spend : 0;
const computeAov = (r: Record<string, number>) => r.purchases > 0 ? r.purchaseValue / r.purchases : 0;
const computeConvRate = (r: Record<string, number>) => r.clicks > 0 ? (r.purchases / r.clicks) * 100 : 0;

export const KPI_CONFIGS: KpiWidgetConfig[] = [
  { id: "kpi-spend", title: "Total Spend", group: "Spend & Delivery", summaryField: "spend", trendField: "spend", formatter: formatCurrency },
  { id: "kpi-impressions", title: "Impressions", group: "Spend & Delivery", summaryField: "impressions", trendField: "impressions", formatter: formatNumber },
  { id: "kpi-reach", title: "Reach", group: "Spend & Delivery", summaryField: "reach", trendField: "reach", formatter: formatNumber },
  { id: "kpi-frequency", title: "Frequency", group: "Spend & Delivery", summaryField: "frequency", trendField: "impressions", sparklineCompute: (r) => r.reach > 0 ? r.impressions / r.reach : 0, formatter: formatDecimal },
  { id: "kpi-cpm", title: "CPM", group: "Spend & Delivery", summaryField: "cpm", sparklineCompute: computeCpm, formatter: formatCurrency },
  { id: "kpi-cpp", title: "CPP", group: "Spend & Delivery", summaryField: "cpp", trendField: "spend", sparklineCompute: (r) => r.purchases > 0 ? r.spend / r.purchases : 0, formatter: formatCurrency },

  { id: "kpi-clicks", title: "Clicks", group: "Click & Traffic", summaryField: "clicks", trendField: "clicks", formatter: formatNumber },
  { id: "kpi-ctr", title: "CTR", group: "Click & Traffic", summaryField: "ctr", sparklineCompute: computeCtr, formatter: formatPercent },
  { id: "kpi-cpc", title: "CPC", group: "Click & Traffic", summaryField: "cpc", sparklineCompute: computeCpc, formatter: formatCurrency, invertColors: true },
  { id: "kpi-landing-page-views", title: "Landing Page Views", group: "Click & Traffic", summaryField: "landingPageViews", trendField: "clicks", formatter: formatNumber },
  { id: "kpi-outbound-ctr", title: "Outbound CTR", group: "Click & Traffic", summaryField: "outboundCtr", sparklineCompute: computeCtr, formatter: formatPercent },

  { id: "kpi-purchases", title: "Purchases", group: "Conversion", summaryField: "purchases", trendField: "purchases", formatter: formatNumber },
  { id: "kpi-revenue", title: "Revenue", group: "Conversion", summaryField: "purchaseValue", trendField: "purchaseValue", formatter: formatCurrency },
  { id: "kpi-roas", title: "ROAS", group: "Conversion", summaryField: "roas", sparklineCompute: computeRoas, formatter: formatRoas },
  { id: "kpi-add-to-cart", title: "Add to Cart", group: "Conversion", summaryField: "addToCart", trendField: "purchases", formatter: formatNumber },
  { id: "kpi-checkout", title: "Checkout Initiated", group: "Conversion", summaryField: "checkoutInitiated", trendField: "purchases", formatter: formatNumber },
  { id: "kpi-leads", title: "Leads", group: "Conversion", summaryField: "leads", trendField: "clicks", formatter: formatNumber },
  { id: "kpi-cost-per-lead", title: "Cost per Lead", group: "Conversion", summaryField: "costPerLead", trendField: "spend", formatter: formatCurrency, invertColors: true },

  { id: "kpi-aov", title: "AOV", group: "Calculated", summaryField: "aov", sparklineCompute: computeAov, formatter: formatCurrency },
  { id: "kpi-net-ad-profit", title: "Net Ad Profit", group: "Calculated", summaryField: "netAdProfit", sparklineCompute: (r) => r.purchaseValue - r.spend, formatter: formatCurrency },
  { id: "kpi-mer", title: "MER", group: "Calculated", summaryField: "mer", sparklineCompute: computeRoas, formatter: formatRoas },
  { id: "kpi-conversion-rate", title: "Conversion Rate", group: "Calculated", summaryField: "conversionRate", sparklineCompute: computeConvRate, formatter: formatPercent },

  { id: "kpi-video-views", title: "Video Views", group: "Video", summaryField: "videoViews", trendField: "impressions", formatter: formatNumber },
  { id: "kpi-thumbstop-rate", title: "Thumbstop Rate", group: "Video", summaryField: "thumbstopRate", trendField: "impressions", formatter: formatPercent },
  { id: "kpi-hook-rate", title: "Hook Rate", group: "Video", summaryField: "hookRate", trendField: "impressions", formatter: formatPercent },
  { id: "kpi-hold-rate", title: "Hold Rate", group: "Video", summaryField: "holdRate", trendField: "impressions", formatter: formatPercent },
  { id: "kpi-cost-per-thruplay", title: "Cost per ThruPlay", group: "Video", summaryField: "costPerThruplay", trendField: "spend", formatter: formatCurrency, invertColors: true },

  { id: "kpi-engagement", title: "Post Engagement", group: "Engagement", summaryField: "postEngagement", trendField: "clicks", formatter: formatNumber },
  { id: "kpi-reactions", title: "Reactions", group: "Engagement", summaryField: "postReactions", trendField: "clicks", formatter: formatNumber },
  { id: "kpi-shares", title: "Shares", group: "Engagement", summaryField: "postShares", trendField: "clicks", formatter: formatNumber },
  { id: "kpi-comments", title: "Comments", group: "Engagement", summaryField: "postComments", trendField: "clicks", formatter: formatNumber },
];

export const WIDGET_REGISTRY: WidgetDefinition[] = [
  ...KPI_CONFIGS.map(k => ({
    id: k.id,
    title: k.title,
    category: "kpi" as const,
    defaultW: 3,
    defaultH: 3,
    minW: 2,
    minH: 2,
    description: `${k.group} metric`,
  })),

  { id: "chart-spend-roas-trend", title: "Spend & ROAS Trend", category: "chart", defaultW: 8, defaultH: 7, minW: 6, minH: 4, description: "Dual-axis spend and ROAS over time" },
  { id: "chart-purchases-by-day", title: "Purchases by Day", category: "chart", defaultW: 4, defaultH: 7, minW: 3, minH: 4, description: "Purchases grouped by day of week" },
  { id: "chart-revenue-trend", title: "Revenue Trend", category: "chart", defaultW: 6, defaultH: 7, minW: 4, minH: 4, description: "Revenue area chart over time" },
  { id: "chart-spend-pacing", title: "Spend Pacing", category: "chart", defaultW: 4, defaultH: 7, minW: 3, minH: 4, description: "Budget pacing donut with status" },
  { id: "chart-placement", title: "Placement Breakdown", category: "chart", defaultW: 6, defaultH: 7, minW: 4, minH: 4, description: "Horizontal bars by placement" },
  { id: "chart-device", title: "Device Breakdown", category: "chart", defaultW: 4, defaultH: 7, minW: 3, minH: 4, description: "Donut chart by device" },
  { id: "chart-age-gender", title: "Age & Gender Heatmap", category: "chart", defaultW: 6, defaultH: 7, minW: 4, minH: 4, description: "Grid heatmap by demographics" },
  { id: "chart-creative-format", title: "Creative Format", category: "chart", defaultW: 6, defaultH: 7, minW: 4, minH: 4, description: "Grouped bars by ad format" },
  { id: "chart-dayparting", title: "Dayparting Heatmap", category: "chart", defaultW: 8, defaultH: 7, minW: 6, minH: 4, description: "24-hour performance heatmap" },

  { id: "table-campaigns", title: "Campaign Performance", category: "table", defaultW: 12, defaultH: 7, minW: 6, minH: 3, description: "All campaigns with metrics" },
  { id: "table-top-ads", title: "Top Ads", category: "table", defaultW: 12, defaultH: 7, minW: 6, minH: 3, description: "Best performing ads" },
  { id: "table-creative-fatigue", title: "Creative Fatigue", category: "table", defaultW: 12, defaultH: 7, minW: 6, minH: 3, description: "Ads showing fatigue signals" },
  { id: "table-quality-scores", title: "Quality Scores", category: "table", defaultW: 12, defaultH: 7, minW: 6, minH: 3, description: "Ad quality rankings" },
  { id: "table-attribution", title: "Attribution Comparison", category: "table", defaultW: 12, defaultH: 6, minW: 6, minH: 3, description: "Attribution window comparison" },
  { id: "table-geo", title: "Geographic Breakdown", category: "table", defaultW: 12, defaultH: 7, minW: 6, minH: 3, description: "Performance by country/region" },

  { id: "chart-platform-spend", title: "Spend by Platform", category: "chart", defaultW: 4, defaultH: 7, minW: 3, minH: 4, description: "Platform spend distribution donut", crossPlatform: true },
  { id: "chart-platform-roas", title: "ROAS by Platform", category: "chart", defaultW: 4, defaultH: 7, minW: 3, minH: 4, description: "ROAS comparison across platforms", crossPlatform: true },
  { id: "chart-cross-platform-trend", title: "Cross-Platform Trend", category: "chart", defaultW: 8, defaultH: 7, minW: 6, minH: 4, description: "Multi-platform spend & revenue trend", crossPlatform: true },
  { id: "chart-conversion-funnel", title: "Conversion Funnel", category: "chart", defaultW: 4, defaultH: 7, minW: 3, minH: 4, description: "Cross-platform conversion funnel", crossPlatform: true },
  { id: "card-blended-roas", title: "Blended ROAS", category: "kpi", defaultW: 3, defaultH: 5, minW: 3, minH: 4, description: "Blended ROAS across all platforms", crossPlatform: true },
  { id: "table-unified-top-ads", title: "Top Ads (Cross-Platform)", category: "table", defaultW: 12, defaultH: 7, minW: 6, minH: 3, description: "Best performing ads across all platforms with platform badges", crossPlatform: true },
  { id: "chart-video-performance", title: "Video View Funnel", category: "chart", defaultW: 6, defaultH: 7, minW: 4, minH: 4, description: "Video view funnel (P25/P50/P75/P100) from real ad insights", crossPlatform: true },
  { id: "chart-platform-comparison", title: "Cross-Platform Spend Comparison", category: "chart", defaultW: 6, defaultH: 7, minW: 4, minH: 4, description: "Spend vs revenue comparison by platform", crossPlatform: true },
  { id: "card-tiktok-engagement", title: "TikTok Engagement", category: "kpi", defaultW: 4, defaultH: 7, minW: 3, minH: 5, description: "TikTok-specific engagement metrics overview", crossPlatform: true },
];

export function getWidgetDef(id: string): WidgetDefinition | undefined {
  return WIDGET_REGISTRY.find(w => w.id === id);
}

export function getKpiConfig(id: string): KpiWidgetConfig | undefined {
  return KPI_CONFIGS.find(k => k.id === id);
}

export const DEFAULT_WIDGET_IDS = [
  "kpi-spend", "kpi-roas", "kpi-purchases", "kpi-ctr",
  "kpi-impressions", "kpi-cpc", "kpi-revenue", "kpi-frequency",
  "chart-spend-roas-trend", "chart-purchases-by-day",
  "chart-placement", "chart-device",
  "table-top-ads",
  "table-creative-fatigue", "table-attribution",
  "chart-age-gender", "chart-creative-format",
  "table-campaigns",
];

export function buildDefaultLayout() {
  const layout: Array<{ i: string; x: number; y: number; w: number; h: number; minW?: number; minH?: number }> = [];

  const push = (id: string, x: number, y: number, w?: number, h?: number) => {
    const def = getWidgetDef(id)!;
    layout.push({ i: id, x, y, w: w ?? def.defaultW, h: h ?? def.defaultH, minW: def.minW, minH: def.minH });
  };

  push("kpi-spend", 0, 0);
  push("kpi-roas", 3, 0);
  push("kpi-purchases", 6, 0);
  push("kpi-ctr", 9, 0);
  push("kpi-impressions", 0, 3);
  push("kpi-cpc", 3, 3);
  push("kpi-revenue", 6, 3);
  push("kpi-frequency", 9, 3);

  push("chart-spend-roas-trend", 0, 6, 8, 7);
  push("chart-purchases-by-day", 8, 6, 4, 7);

  push("chart-placement", 0, 13, 7, 7);
  push("chart-device", 7, 13, 5, 7);

  push("table-top-ads", 0, 20, 12, 7);

  push("table-creative-fatigue", 0, 27, 6, 7);
  push("table-attribution", 6, 27, 6, 6);

  push("chart-age-gender", 0, 34, 6, 7);
  push("chart-creative-format", 6, 34, 6, 7);

  push("table-campaigns", 0, 41, 12, 7);

  return layout;
}
